import { useEffect, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FormFields,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
} from '@product-framework/index';
import { editSchema } from '../../schemas';
import validationSchema from './validations';
import FormViewer from '../../components/FormViewer';

function ServiceJobFormEdit() {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId } = useParams();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/service-job-forms/${pageId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/service-job-forms/${pageId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);

  const preProcessRequestData = data => {
    const {
      createdByUserId,
      id,
      lastModifiedByUserId,
      formUiSchema,
      checkOrder,
      ...other
    } = data;
    return {
      formUiSchema: formUiSchema ? JSON.parse(formUiSchema) : {},
      checkOrder: parseInt(data.checkOrder, 10) || 1,
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const {
      createdByUserId,
      id,
      lastModifiedByUserId,
      formUiSchema,
      checkOrder,
      ...other
    } = data;
    return {
      ...other,
      formUiSchema: formUiSchema ? JSON.stringify(formUiSchema, 2, 1) : {},
      checkOrder: parseInt(data.checkOrder, 10) || 1,
    };
  };

  const submitEditForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEJOBFORM_UPDATE_SUCCESS',
          defaultMessage: 'Job Form was saved successfully',
        }),
      );
      setInitialValues(preProcessFetchData(response?.data));
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEJOBFORM_UPDATE_FAILURE',
          defaultMessage: 'Error: Could not save the job form',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    setFormErrors(null);
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(response?.data),
      });
      setLoading(false);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEJOBFORM_DATA_FETCH_FAILURE',
          defaultMessage: 'Error: Could not fetch the job form data',
        }),
      });
    }
  };

  useEffect(() => {
    fetchInitialData(pageId);
  }, [pageId]);

  const formProps = {
    customComponents: { ...FormFields, FormViewer },
    schema: editSchema,
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOBFORMS_BREADCRUMB',
        defaultMessage: 'Job Forms',
      }),
      icon: IconLibrary.ServiceJobForms,
      onClick: () => {
        navigate('/servicejobforms');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_SERVICEJOBFORM_EDIT_BREADCRUMB',
        defaultMessage: 'Edit Job Form',
      }),
      icon: IconLibrary.ServiceJobForm,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_SERVICEJOBFORMS_STACKABLE_BUTTON',
        defaultMessage: 'Create Job Form',
      }),
      Icon: IconLibrary.ServiceJobForm,
      variant: 'outlined',
      onClick: () => navigate('/servicejobforms/create'),
    },
    {
      label: formatMessage({
        id: 'EDIT_SERVICEJOBFORMS_STACKABLE_BUTTON',
        defaultMessage: 'List Job Forms',
      }),
      Icon: IconLibrary.ServiceJobForms,
      variant: 'outlined',
      onClick: () => navigate('/servicejobforms'),
    },
  ];

  return (
    <Layout>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing..." />
        </LoadingEvent>
      )}

      {!loading && (
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
      )}

      {!loading && (
        <PageSection
          label={formatMessage({
            id: 'SERVICEJOBFORM_EDIT_HEADING',
            defaultMessage: 'Edit Job Form',
          })}>
          <Box>
            <CRUDComponent
              errors={formErrors}
              type={CRUD_COMPONENT_TYPE.FORM}
              formProps={formProps}>
              <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
                <Button
                  variant="contained"
                  startIcon={<IconLibrary.Save />}
                  color="primary"
                  onClick={() => editFormRef.current?.submit()}>
                  {formatMessage({
                    id: 'SERVICEJOBFORM_UPDATE_SAVE_BUTTON',
                    defaultMessage: 'Save Job Form',
                  })}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<IconLibrary.Reset />}
                  onClick={() => editFormRef.current?.reset()}>
                  {formatMessage({
                    id: 'SERVICEJOBFORM_UPDATE_RESET_BUTTON',
                    defaultMessage: 'Reset',
                  })}
                </Button>
              </Box>
            </CRUDComponent>
          </Box>
        </PageSection>
      )}
    </Layout>
  );
}

export default observer(ServiceJobFormEdit);
